<script>
  import Page from '@components/page'
  import Table from '@components/table'
  import Button from '@components/buttons/button.svelte'
  import Dropdown from '@components/general/dropdown.svelte'
  import SyncModal from '@components/live_sessions/sync-modal.svelte'
  import ContributorLink from '@components/contributors/contributor-link.svelte'
  import { useSelection } from '@hooks/use-selection.svelte'
  import { router } from '@inertiajs/svelte'
  import { RefreshCw, ChevronLeft, Trash2, Wand, Pencil, Users, ExternalLink } from 'lucide-svelte'
  import Tag from '@components/general/tag.svelte'
  import { formatDate, formatTime } from '@lib/utils/date'
  import { colors } from '@lib/utils/colors'
  import { cn, sortItems } from '@lib/utils'
  import { usePageParam } from '@hooks/use-page-param.svelte'
  import ago from 's-ago'

  let { live_sessions = [], pagination, ids, last_synced_at } = $props()

  let selection = $derived(useSelection({ initialItems: live_sessions, allIds: ids }))
  let sort = usePageParam({ key: "sort", defaultValue: "newest", items: sortItems })

  let sync = $state({ open: false, processing: false, results: null, error: null })

  let syncButtonHint = $derived(last_synced_at ? `Last synced ${ago(new Date(last_synced_at))}` : "Never synced")

  function batchDelete() {
    if (!window.confirm(`Delete the selected live sessions?`)) return
    router.visit(`/admin/live_sessions/batch_delete`, { method: "delete", data: { ids: selection.ids } }, { onSuccess: selection.clear() })
  }

  function resetSync() {
    sync = { open: false, processing: false, results: null, error: null }
  }

</script>

<Page.Layout title="Live Sessions">
  <Page.Navigation title="Live Sessions" links={[{ text: "Admin", href: "/admin" }]} />

  <Page.Titles title="Live Sessions" subtitle="Manage your live sessions">
    {#snippet buttons()}
      <Button text="Public page" path="/live-sessions" BeforeIcon={ChevronLeft} />
      <Button onclick={() => sync.open = true} text="Arlo sync" Icon={RefreshCw} hint={syncButtonHint} />
    {/snippet}
  </Page.Titles>

  <Page.Content>

    <Table.FilterBar {pagination} {selection}>
      {#snippet filters()}

        <Dropdown buttonProps={{ text: sort.label || "Sort", size: "md", Icon: sort.icon }} width="16rem" align="start" itemSize="sm">
          {#snippet contents(item)}
            {#each sort.items as sortItem (sortItem.value)}
              {@render item({ text: sortItem.label, icon: sortItem.icon, onSelect: () => { selection.clear(); sort.setValue(sortItem.value) } })}
            {/each}
          {/snippet}
        </Dropdown>

        {#if selection.populated}
          <Dropdown buttonProps={{ text: `${selection.count} selected`, size: "md", Icon: Wand }} width="16rem" align="start" itemSize="sm">
            {#snippet contents(item)}
              {@render item({ text: "Delete from platform", icon: Trash2, onSelect: () => batchDelete() })}
            {/snippet}
          </Dropdown>
        {/if}

      {/snippet}
    </Table.FilterBar>

    <Table.Root empty={!live_sessions.length} emptyMessage="No live sessions found">

      <Table.Head>
        <Table.SelectionDropdownCell {selection} />
        <Table.Header width="35%">Event</Table.Header>
        <Table.Header width="20%">Date & Time</Table.Header>
        <Table.Header width="20%">Presenter</Table.Header>
        <Table.Header width="12%" align="center">Enrolled</Table.Header>
        <Table.Header width="8%" align="center">Actions</Table.Header>
      </Table.Head>

      <Table.Body>
        {#each live_sessions as live_session (live_session.id)}
          <Table.Row selected={selection.has(live_session.id)}>

            <!-- Checkbox -->
            <Table.Cell width="3rem" align="center">
              <input type="checkbox" checked={selection.has(live_session.id)} onclick={e => selection.toggle(live_session.id, e)} />
            </Table.Cell>

            <!-- Event -->
            <Table.Cell width="35%">
              <div class="flex items-center gap-2 group">
                <div class={cn(colors[live_session.color || "gray"]?.base, "w-2.5 h-2.5 rounded-full shrink-0")}></div>
                <div class="flex flex-col gap-0.5 min-w-0">
                  <div class="flex items-center gap-1">
                    <Button variant="link" class="text-gray-800 font-medium leading-none" path={`/admin/live_sessions/${live_session.id}/edit`}>{live_session.name}</Button>
                    <Button variant="naked" Icon={ExternalLink} hint="View" path={`/admin/live_sessions/${live_session.id}`} class="text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" iconProps={{ size: 12 }} />
                  </div>
                  {#if live_session.arlo_code}
                    <span class="text-xs text-gray-400">{live_session.arlo_code}</span>
                  {/if}
                  {#if live_session.tags?.length > 0}
                    <div class="flex flex-wrap gap-1 mt-0.5">
                      {#each live_session.tags as tag (tag.id)}
                        <Tag {tag} size="xxs" variant="faint" />
                      {/each}
                    </div>
                  {/if}
                  </div>
              </div>
            </Table.Cell>
            
            <!-- Date & Time -->
            <Table.Cell width="20%">
              <div class="flex flex-col gap-0.5">
                <span>{formatDate(live_session.start_time)}</span>
                <span class="text-xs text-gray-400">{formatTime(live_session.start_time)} – {formatTime(live_session.end_time)}</span>
              </div>
            </Table.Cell>
            
            <!-- Presenter -->
            <Table.Cell width="20%">
              <ContributorLink contributor={live_session.contributor} admin />
            </Table.Cell>
            
            <!-- Enrolled -->
            <Table.Cell width="12%" align="center">
              {#if live_session.enrollments?.length}
                <Button variant="link" class="inline-flex items-center gap-1 text-xs text-gray-600" 
                  text={live_session.enrollments.length} 
                  Icon={Users}
                  path={`/admin/live_sessions/${live_session.id}`} />
              {:else}
                <span class="text-xs text-gray-300">—</span>
              {/if}
            </Table.Cell>

            <!-- Actions -->
            <Table.Cell width="8%" align="center">
              <Button variant="naked" Icon={Pencil} hint="Edit" path={`/admin/live_sessions/${live_session.id}/edit`} class="justify-center" />
            </Table.Cell>

          </Table.Row>
        {/each}
      </Table.Body>

    </Table.Root>

    <Table.Pagination {pagination} />

  </Page.Content>

</Page.Layout>

<SyncModal {sync} lastSyncText={syncButtonHint} reset={resetSync} />
